// Cosmetic sea: one large plane at the water surface, shaded entirely in the fragment shader.
// Human-facing only. The simulation treats the seabed as ordinary ground (the quadruped can
// wade or walk out), so none of this feeds back.
//
// What the shader does, per pixel:
//   depth     sea level minus the ground height, sampled from the same heightfield texture the
//             grass uses (clamped at the edge, so beyond the world the sea is as deep as its rim).
//   colour    shallow turquoise to deep blue by depth, with opacity rising from 0 at the
//             waterline, so the sand shows through the shallows.
//   waves     a few analytic sine swells plus fine ripples give the normal (no geometry). In
//             shallow water the swell direction bends toward the shore (the heightfield's uphill
//             direction), so the crests arrive parallel to the beach.
//   foam      bands that travel shoreward across the shallows, broken up by noise, and a thin
//             swash line right at the waterline.
//   light     a MeshStandardMaterial, so the sun's glint, ambient light, fog and tone mapping all
//             come from the scene like everything else. Sky reflection (Fresnel) is mixed in
//             AFTER tone mapping, from the display-range sky colour the renderer already
//             computes, so the far water melts into the horizon fog.
//   glitter   the sun's and the moon's reflection off the wave facets, drawn after tone mapping. A
//             Beckmann lobe on the same animated normal, widened with distance so far water gives a
//             broad shimmering path instead of aliased sparks. Each light's strength arrives as a
//             vector K in the sky curve's units (SKY_EXPOSURE * display gain * beam irradiance), so
//             the sun saturates to white and a full moon gives a soft silver path. The scene's
//             directional light only exists for the sun, so the moon's path is made here.
//   below     seen from underneath (or with the eye under the surface) the plane becomes the
//             underside of the sea: a dark teal ceiling with a bright Snell's window overhead.
//             The renderer tints the scene fog to match while the eye is under.
//
// The surface height (sea level plus tide) is set by the renderer every frame. Sim (x, y, z)
// maps to three (x, z, -y), so the sea surface is a horizontal plane at three y.
import * as THREE from 'three';
import type { TerrainField } from './grass';

const PLANE_M = 1800;            // metres on a side; the fog has swallowed the sea long before its edge
const SNAP_M = 16;               // the plane follows the eye in steps of this (waves use world coordinates, so nothing swims)

// Look tuning. Colours are linear RGB.
const SHALLOW = [0.030, 0.420, 0.400] as const;
const DEEP    = [0.004, 0.060, 0.140] as const;
const ABSORB_COLOUR = 0.55;      // 1/metres: how fast the colour goes from shallow to deep with depth
const ABSORB_ALPHA  = 1.15;      // 1/metres: how fast the water goes opaque with depth
const GLITTER_GAIN  = 0.05;      // scales the reflection lobe (its peak is about 1 / (pi sigma^2) before this)
const MOON_GLITTER_BOOST = 8.0;  // the moon's beam is a million times dimmer than the sun's; a reflection of it is
                                 // judged against a dark sea, so it is lifted. 1 = the sky curve's own value.
const GLITTER_SIGMA_NEAR = 0.05; // lobe width (rms slope) at the eye
const GLITTER_SIGMA_FAR  = 0.30; // ... and at the far end, where the facets are too small to resolve

const WATER_COMMON = /* glsl */`
uniform sampler2D uField;
uniform vec4  uFieldInfo;      // nx, ny, width, height
uniform float uSeaY;
uniform float uWT;
uniform vec2  uWaveDir;
uniform vec3  uShallow;
uniform vec3  uDeep;
uniform vec3  uSky;            // display-range linear sky colour
uniform vec3  uUnderCol;
uniform float uUnder;          // 1 when the eye is under the surface
uniform vec3  uSunDir;         // unit vectors toward the sun and the moon, three axes (y up)
uniform vec3  uSunK;           // their strength per channel, in the sky curve's units (0 when below the horizon)
uniform vec3  uMoonDir;
uniform vec3  uMoonK;
varying vec3  vWPos;

float wFoam;
vec3  wN;

float wGround(vec2 xz) {
  vec2 uv = vec2(xz.x / uFieldInfo.z * (uFieldInfo.x - 1.0), -xz.y / uFieldInfo.w * (uFieldInfo.y - 1.0));
  uv = clamp(uv, vec2(0.0), uFieldInfo.xy - 1.0001);
  ivec2 i = ivec2(floor(uv));
  vec2 f = uv - vec2(i);
  float a = texelFetch(uField, i, 0).r;
  float b = texelFetch(uField, i + ivec2(1, 0), 0).r;
  float c = texelFetch(uField, i + ivec2(0, 1), 0).r;
  float d = texelFetch(uField, i + ivec2(1, 1), 0).r;
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float wHash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float wNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(wHash(i), wHash(i + vec2(1.0, 0.0)), f.x),
             mix(wHash(i + vec2(0.0, 1.0)), wHash(i + vec2(1.0, 1.0)), f.x), f.y);
}
// Gradient of the surface height at p (metres per metre): five sine swells from long to fine.
// d0 is the travel direction of the main swell; the others fan out from it. hf scales the finest
// two (faded with distance so far water does not shimmer).
vec2 wGrad(vec2 p, vec2 d0, float t, float hf) {
  vec2 d1 = vec2(d0.x * 0.8192 - d0.y * 0.5736, d0.x * 0.5736 + d0.y * 0.8192);
  vec2 d2 = vec2(d0.x * 0.6428 + d0.y * 0.7660, -d0.x * 0.7660 + d0.y * 0.6428);
  vec2 d3 = vec2(-d0.y, d0.x);
  float p0 = dot(d0, p) * 0.55 + t * 1.05;
  float p1 = dot(d1, p) * 1.30 + t * 1.70;
  float p2 = dot(d2, p) * 2.90 + t * 2.60;
  float p3 = dot(d3, p) * 6.10 + t * 3.40;
  float p4 = dot(d0, p) * 11.0 - t * 4.10 + 1.3;
  return d0 * (0.050 * 0.55 * cos(p0)) + d1 * (0.022 * 1.30 * cos(p1)) + d2 * (0.012 * 2.90 * cos(p2))
       + hf * (d3 * (0.0045 * 6.10 * cos(p3)) + d0 * (0.0025 * 11.0 * cos(p4)));
}
`;

// Reflection of a distant light off the wave facets. L: toward the light, K: its strength, N: the
// facet normal, V: toward the eye, dist: eye to surface point. Returns display-range colour.
const WATER_GLITTER = /* glsl */`
vec3 wGlitter(vec3 L, vec3 K, vec3 N, vec3 V, float dist) {
  if (K.r + K.g + K.b <= 0.0 || L.y <= 0.0) return vec3(0.0);
  float sg = mix(${GLITTER_SIGMA_NEAR.toFixed(3)}, ${GLITTER_SIGMA_FAR.toFixed(3)}, smoothstep(30.0, 600.0, dist));
  vec3 H = normalize(L + V);
  float nh = clamp(dot(N, H), 1e-3, 1.0);
  float t2 = (1.0 - nh * nh) / (nh * nh);
  float D = exp(-t2 / (sg * sg)) / (3.14159265 * sg * sg * nh * nh * nh * nh);
  float vh = clamp(dot(V, H), 0.0, 1.0);
  float F = 0.02 + 0.98 * pow(1.0 - vh, 5.0);
  float up = smoothstep(0.0, 0.06, L.y);                    // fades out as the light meets the horizon
  return 1.0 - exp(-K * (D * F * up * ${GLITTER_GAIN.toFixed(3)}));
}
`;

const WATER_COLOR = /* glsl */`
#include <color_fragment>
{
  vec2 wp = vWPos.xz;
  float wDepth = uSeaY - wGround(wp);
  if (wDepth <= 0.0) discard;                       // the plane lies under the dry land: nothing to draw

  // Shoreward swell: where the water is shallow, bend the travel direction toward the uphill
  // direction of the ground so the crests run parallel to the beach.
  vec2 gg = vec2(wGround(wp + vec2(0.7, 0.0)) - wGround(wp - vec2(0.7, 0.0)),
                 wGround(wp + vec2(0.0, 0.7)) - wGround(wp - vec2(0.0, 0.7)));
  float gl = length(gg);
  vec2 shore = gl > 1e-4 ? gg / gl : uWaveDir;
  float shoal = 1.0 - smoothstep(1.2, 4.5, wDepth);
  vec2 wdir = normalize(mix(uWaveDir, shore, shoal * 0.85));

  float dist = distance(cameraPosition, vWPos);
  float hf = 1.0 - smoothstep(25.0, 90.0, dist);
  vec2 g = wGrad(wp, wdir, uWT, hf);
  // fine ripples from two scrolling noise fields (stand in for wind chop), fading with distance
  vec2 rp = wp * 1.9 + uWT * vec2(0.25, 0.12);
  vec2 rg = vec2(wNoise(rp + vec2(0.37, 0.0)) - wNoise(rp - vec2(0.37, 0.0)),
                 wNoise(rp + vec2(0.0, 0.37)) - wNoise(rp - vec2(0.0, 0.37)));
  g += rg * (0.10 * hf);
  wN = normalize(vec3(-g.x, 1.0, -g.y));
  if (!gl_FrontFacing) wN = -wN;

  // Colour and opacity by depth.
  float depthT = 1.0 - exp(-wDepth * ${ABSORB_COLOUR.toFixed(2)});
  vec3 body = mix(uShallow, uDeep, pow(depthT, 0.8));
  float alpha = (1.0 - exp(-wDepth * ${ABSORB_ALPHA.toFixed(2)})) * smoothstep(0.0, 0.05, wDepth);

  // Foam: bands that travel shoreward through the shallows, broken up by noise, plus a swash line.
  float ph = wDepth * 1.5 + uWT * 1.1 + sin(wp.x * 0.35 + wp.y * 0.27) * 1.6;
  float band = 0.5 + 0.5 * sin(ph * 2.4);
  float breaker = 1.0 - smoothstep(0.35, 1.4, wDepth);
  float swash = 1.0 - smoothstep(0.02, 0.14 + 0.05 * sin(uWT * 0.9 + wp.x * 0.5), wDepth);
  float fn = wNoise(wp * 3.0 + uWT * 0.2) * 0.65 + wNoise(wp * 7.0 - uWT * 0.15) * 0.35;
  wFoam = clamp((band * breaker * 0.9 + swash) * smoothstep(0.30, 0.70, fn), 0.0, 1.0);
  diffuseColor.rgb = mix(body, vec3(0.90), wFoam);
  diffuseColor.a = max(alpha, wFoam * 0.92);
}
`;

const WATER_ROUGH = /* glsl */`
float roughnessFactor = mix(0.045, 0.9, wFoam);
`;

const WATER_NORMAL = /* glsl */`
#include <normal_fragment_begin>
normal = normalize((viewMatrix * vec4(wN, 0.0)).xyz);
`;

const WATER_POST = /* glsl */`
#include <tonemapping_fragment>
{
  vec3 wV = normalize(cameraPosition - vWPos);
  if (!gl_FrontFacing || uUnder > 0.5) {
    // The underside of the surface: a dark ceiling with a bright window where the sky shows
    // through (Snell: about 48 degrees from straight up), mirror-dark beyond it.
    float win = smoothstep(0.62, 0.74, -wV.y);
    vec3 ceilingCol = mix(uUnderCol * 0.8, uSky * vec3(0.75, 0.95, 1.0), win);
    gl_FragColor = vec4(ceilingCol, 0.96);
  } else {
    float fr = 0.025 + 0.975 * pow(1.0 - clamp(dot(wN, wV), 0.0, 1.0), 5.0);
    gl_FragColor.rgb = mix(gl_FragColor.rgb, uSky, fr * 0.85 * (1.0 - wFoam));
    gl_FragColor.a = max(gl_FragColor.a, fr * 0.9);
    // sun and moon glitter, screen-blended on top (foam is matte and takes little of it)
    float wDist = distance(cameraPosition, vWPos);
    vec3 gl = (wGlitter(uSunDir, uSunK, wN, wV, wDist) + wGlitter(uMoonDir, uMoonK, wN, wV, wDist)) * (1.0 - 0.85 * wFoam);
    gl_FragColor.rgb += (vec3(1.0) - gl_FragColor.rgb) * clamp(gl, 0.0, 1.0);
    gl_FragColor.a = max(gl_FragColor.a, max(gl.r, max(gl.g, gl.b)));
  }
}
`;

/** The two lights that glitter on the sea. Directions are unit vectors in three's axes (y up); K is
 *  SKY_EXPOSURE * the display gain * the beam's per-channel irradiance (the renderer's sky curve). */
export interface WaterLights {
  sunDir: THREE.Vector3; sunK: ArrayLike<number>;
  moonDir?: THREE.Vector3 | null; moonK?: ArrayLike<number> | null;
}

export class WaterView {
  readonly mesh: THREE.Mesh;
  private readonly scene: THREE.Scene;
  private readonly geo: THREE.PlaneGeometry;
  private readonly material: THREE.MeshStandardMaterial;
  private fieldTex: THREE.DataTexture | null = null;
  private bw = 128; private bh = 128;
  private readonly u = {
    uField:     { value: null as THREE.Texture | null },
    uFieldInfo: { value: new THREE.Vector4(2, 2, 1, 1) },
    uSeaY:      { value: 0 },
    uWT:        { value: 0 },
    uWaveDir:   { value: new THREE.Vector2(0.80, -0.60).normalize() },
    uShallow:   { value: new THREE.Vector3(...SHALLOW) },
    uDeep:      { value: new THREE.Vector3(...DEEP) },
    uSky:       { value: new THREE.Vector3(0.5, 0.6, 0.8) },
    uUnderCol:  { value: new THREE.Vector3(0.02, 0.2, 0.25) },
    uUnder:     { value: 0 },
    uSunDir:    { value: new THREE.Vector3(0, 1, 0) },
    uSunK:      { value: new THREE.Vector3() },
    uMoonDir:   { value: new THREE.Vector3(0, 1, 0) },
    uMoonK:     { value: new THREE.Vector3() },
  };

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.geo = new THREE.PlaneGeometry(PLANE_M, PLANE_M, 1, 1);
    this.geo.rotateX(-Math.PI / 2);                  // lie flat on the XZ plane
    const m = new THREE.MeshStandardMaterial({
      color: 0xffffff, roughness: 0.05, metalness: 0.0,
      transparent: true, depthWrite: false, side: THREE.DoubleSide,
    });
    m.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, this.u);
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vWPos;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\n' + WATER_COMMON + WATER_GLITTER)
        .replace('#include <color_fragment>', WATER_COLOR)
        .replace('#include <roughnessmap_fragment>', WATER_ROUGH)
        .replace('#include <normal_fragment_begin>', WATER_NORMAL)
        .replace('#include <tonemapping_fragment>', WATER_POST);
    };
    m.customProgramCacheKey = () => 'water-v2-glitter';
    this.material = m;
    this.mesh = new THREE.Mesh(this.geo, m);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.renderOrder = 2;                       // after the opaque scene, so the shallows blend over the sand
    this.mesh.visible = false;                       // shown once there is terrain with a sea
    this.setTerrain(null);
    scene.add(this.mesh);
  }

  /** Use the terrain heightfield for depth; null = no ground info (the sea stays hidden). */
  setTerrain(f: TerrainField | null) {
    if (this.fieldTex) { this.fieldTex.dispose(); this.fieldTex = null; }
    let data: Float32Array<ArrayBuffer>, nx: number, ny: number, w: number, h: number;
    if (f) { data = new Float32Array(f.z); nx = f.nx; ny = f.ny; w = f.width; h = f.height; }
    else { data = new Float32Array(4); nx = 2; ny = 2; w = this.bw; h = this.bh; }
    const t = new THREE.DataTexture(data, nx, ny, THREE.RedFormat, THREE.FloatType);
    t.minFilter = THREE.NearestFilter; t.magFilter = THREE.NearestFilter;
    t.generateMipmaps = false; t.needsUpdate = true;
    this.fieldTex = t;
    this.u.uField.value = t;
    this.u.uFieldInfo.value.set(nx, ny, w, h);
    this.bw = w; this.bh = h;
  }

  /** Show or hide the sea (hidden for worlds with no ground below sea level, and by a debug toggle). */
  setEnabled(e: boolean) { this.mesh.visible = e; }

  /**
   * Per frame. `seaY`: three-y of the surface (sea level plus tide). `sky`: the display-range sky
   * colour. `under`: the eye is below the surface. `underCol`: the colour of the water seen from
   * inside, which the renderer also uses for fog.
   */
  update(eye: THREE.Vector3, seaY: number, tSec: number, sky: THREE.Color, under: boolean, underCol: THREE.Color,
         lights?: WaterLights) {
    this.mesh.position.set(Math.round(eye.x / SNAP_M) * SNAP_M, seaY, Math.round(eye.z / SNAP_M) * SNAP_M);
    this.u.uSeaY.value = seaY;
    this.u.uWT.value = tSec;
    this.u.uSky.value.set(sky.r, sky.g, sky.b);
    this.u.uUnderCol.value.set(underCol.r, underCol.g, underCol.b);
    this.u.uUnder.value = under ? 1 : 0;
    // glitter lights; none given (or a light below the horizon) means no reflection from it
    const L = lights;
    if (L && L.sunDir.y > 0) { this.u.uSunDir.value.copy(L.sunDir); this.u.uSunK.value.set(L.sunK[0], L.sunK[1], L.sunK[2]); }
    else this.u.uSunK.value.set(0, 0, 0);
    if (L && L.moonDir && L.moonK && L.moonDir.y > 0) {
      this.u.uMoonDir.value.copy(L.moonDir);
      this.u.uMoonK.value.set(L.moonK[0] * MOON_GLITTER_BOOST, L.moonK[1] * MOON_GLITTER_BOOST, L.moonK[2] * MOON_GLITTER_BOOST);
    } else this.u.uMoonK.value.set(0, 0, 0);
  }

  dispose() {
    this.scene.remove(this.mesh);
    this.geo.dispose(); this.material.dispose();
    this.fieldTex?.dispose();
  }
}
